import { useEffect, useRef, useState } from 'react';
import type { WhatsappGroup } from '@wix/ai-scheduling-assistant-common-lib/entities';

/**
 * The group the settings modal is showing: the LIVE one from the list, never a snapshot.
 *
 * `lastKnown` is consulted for exactly one window — a delete removes the group from the cache
 * optimistically, and without a fallback the modal (and the confirm dialog inside it) would unmount
 * mid-request, so a FAILED delete would replace its inline error with a silently reopened modal. It is
 * scoped to `openId`, so a snapshot left over from a previously opened group can never be shown.
 */
export function resolveOpenGroup(
  openId: string | null,
  groups: WhatsappGroup[],
  lastKnown: WhatsappGroup | null,
): WhatsappGroup | null {
  if (!openId) return null;
  const live = groups.find((group) => group.id === openId);
  if (live) return live;
  return lastKnown?.id === openId ? lastKnown : null;
}

/**
 * Which group's settings modal is open — held as an **id** and resolved from the live list on every
 * render, exactly as iOS (`waStore.groups.first { $0.id == groupId }`) and Android
 * (`WaGroupsStore.groups.firstOrNull { it.id == groupId }`) already do.
 *
 * Holding the group *object* in state is what made the modal go stale: the response-mode toggle, the
 * inline rename and a link reset all write the React-Query cache (`useWhatsappGroupActions`), but a
 * snapshot never sees the new value — so the switch sprang back to its old position (and a renamed
 * group kept its old title, and a reset link kept showing the REVOKED url) until the modal was closed
 * and reopened. The deeplink route `WhatsappGroupScreen` always resolved by id and never had the bug.
 */
export function useOpenWhatsappGroup(groups: WhatsappGroup[]): {
  group: WhatsappGroup | null;
  openGroup: (group: WhatsappGroup) => void;
  closeGroup: () => void;
} {
  const [openId, setOpenId] = useState<string | null>(null);
  const lastKnown = useRef<WhatsappGroup | null>(null);
  const group = resolveOpenGroup(openId, groups, lastKnown.current);

  useEffect(() => {
    if (group) lastKnown.current = group;
  }, [group]);

  return {
    group,
    openGroup: (opened) => {
      lastKnown.current = opened;
      setOpenId(opened.id);
    },
    closeGroup: () => setOpenId(null),
  };
}
